import * as React from 'react';

import { useLocation } from 'js/lib/useRouter';

import PageFooter from 'bundles/page-footer/components/PageFooter';
import PageHeader from 'bundles/page-header/components/PageHeader';
import InternalError from 'bundles/promo-template/components/InternalError';
import NotFound from 'bundles/promo-template/components/NotFound';
import PagePlaceholder from 'bundles/promo-template/components/PagePlaceholder';
import { PAGE_FOOTER_TYPES, PAGE_HEADER_TYPES } from 'bundles/promo-template/constants';
import PageContent from 'bundles/promo-template/contentful/pages/PageContent';
import { PromoTemplateDomainContext } from 'bundles/promo-template/contexts/PromoTemplateDomainContext';
import usePageContentQuery from 'bundles/promo-template/queries/hooks/usePageContentQuery';
import { getPageHeaderProps } from 'bundles/unified-description-page-common/utils/getProductHeaderProps';

type Props = {
  slug: string;
};

const PromoTemplatePage = ({ slug }: Props) => {
  const { domain } = React.useContext(PromoTemplateDomainContext);
  const { loading, page, error } = usePageContentQuery(slug, domain);
  const { pathname } = useLocation();

  if (loading) {
    return <PagePlaceholder />;
  }

  if (error || !page) {
    if (error?.graphQLErrors?.[0]?.extensions?.errorType === 'NOT_FOUND' || !page) {
      return <NotFound />;
    }

    return <InternalError pathname={pathname} />;
  }

  const { hideMetaNav, pageHeaderType, pageFooterType } = page;
  const showSimplifiedPageHeader = pageHeaderType === PAGE_HEADER_TYPES.SIMPLIFIED;
  const showSimplifiedPageFooter = pageFooterType !== PAGE_FOOTER_TYPES.STANDARD;

  const pageHeaderProps = showSimplifiedPageHeader
    ? { ...getPageHeaderProps({ pathname, productSlug: slug, showSimplifiedPageHeader }), hideMetaNav }
    : { hideMetaNav, showGDPRBanner: true, showLanguagesDropdown: true };

  return (
    <>
      <PageHeader {...pageHeaderProps} />
      <main>
        <PageContent slug={slug} domain={domain} pageData={page} />
      </main>
      <PageFooter isDirectoryPage={showSimplifiedPageFooter} />
    </>
  );
};

export default PromoTemplatePage;
